import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const DarkModeContext = createContext();

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

export const DarkModeProvider = ({ children }) => {
  // Safe auth context access with defensive programming
  let authContext = null;
  try {
    authContext = useAuth();
  } catch (error) {
    // AuthContext not available, using fallback
  }
  
  const { user, checkFeatureAccess, loading: authLoading } = authContext || {};
  const [darkMode, setDarkMode] = useState(false);
  const [darkModePreference, setDarkModePreference] = useState('system'); // 'system', 'light', 'dark'

  // Dark mode is now available for all users (free feature)
  const isDarkModeAvailable = authContext && !authLoading && user ? true : false;

  // Detect system preference
  const getSystemPreference = () => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  };

  // Load user preferences from localStorage
  useEffect(() => {
    // Don't initialize dark mode until auth loading is complete AND authContext is available
    if (!authContext || authLoading) {
      return;
    }
    
    if (user && isDarkModeAvailable) {
      const savedPreference = localStorage.getItem(`darkMode_${user.email}`);
      if (savedPreference) {
        const preference = JSON.parse(savedPreference);
        setDarkModePreference(preference);
        
        if (preference === 'system') {
          setDarkMode(getSystemPreference());
        } else {
          setDarkMode(preference === 'dark');
        }
      } else {
        // Default to system preference for new users
        setDarkModePreference('system');
        setDarkMode(getSystemPreference());
      }
    } else {
      // Non-premium users or not logged in - always light mode
      setDarkMode(false);
      setDarkModePreference('light');
    }
  }, [user, isDarkModeAvailable, authLoading, authContext]);

  // Listen to system preference changes
  useEffect(() => {
    if (darkModePreference === 'system' && isDarkModeAvailable) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        setDarkMode(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [darkModePreference, isDarkModeAvailable]);

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode && isDarkModeAvailable) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode, isDarkModeAvailable]);

  // Update dark mode preference
  const updateDarkModePreference = (preference) => {
    if (!authContext || authLoading || !isDarkModeAvailable) {
      return;
    }

    setDarkModePreference(preference);
    
    if (preference === 'system') {
      setDarkMode(getSystemPreference());
    } else {
      setDarkMode(preference === 'dark');
    }

    // Save to localStorage
    if (user) {
      localStorage.setItem(`darkMode_${user.email}`, JSON.stringify(preference));
    }
  };

  const value = {
    darkMode: isDarkModeAvailable ? darkMode : false,
    darkModePreference,
    isDarkModeAvailable,
    updateDarkModePreference,
    toggleDarkMode: () => {
      if (!authContext || authLoading || !isDarkModeAvailable) {
        return;
      }
      const newMode = darkModePreference === 'dark' ? 'light' : 'dark';
      updateDarkModePreference(newMode);
    }
  };

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};

export default DarkModeContext;